// Lean RemotionPreview built on the native <Player> chrome.
//
// The previous 837-line implementation reimplemented play/pause, scrub bar,
// keyboard shortcuts, fullscreen modal, and frame readout — all of which
// the @remotion/player package ships natively (see remotion.dev/docs/player/api).
// This version delegates that chrome to the Player's `controls` prop and
// only keeps the MotionForge-specific UI: zoom, BG picker, content-scale
// slider, resolution dropdown.
//
// LEGACY ESCAPE HATCH:
//   localStorage.setItem("motionforge-preview-legacy", "1") to fall back to
//   the old RemotionPreviewLegacy component. Useful if a regression in the
//   lean version breaks something we haven't migrated yet. Reload after
//   toggling — the choice is read once on mount.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Player, type PlayerRef } from "@remotion/player";
import { useDynamicComponent } from "@/hooks/useDynamicComponent";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { withScaling } from "@/lib/scaled-component";
import {
  RemotionPreviewLegacy,
  ResolutionDropdown,
} from "./RemotionPreviewLegacy";

interface RemotionPreviewProps {
  moduleUrl: string | null;
  inputProps: Record<string, unknown>;
  durationFrames: number;
  fps: number;
  width: number;
  height: number;
  /** Content scale (0.3-1.0) — shrinks graphic to fit overflowing content. */
  contentScale?: number;
  /** Template's native width (for aspect-match detection in the dropdown). */
  baseWidth?: number;
  /** Template's native height (for aspect-match detection in the dropdown). */
  baseHeight?: number;
  /** Fires once when the internal Player ref attaches. */
  onPlayerRef?: (ref: PlayerRef | null) => void;
  /**
   * Optional slot rendered at the right edge of the toolbar, after the
   * canvas-size dropdown. TemplateDetail uses this to mount the primary
   * Render button next to the resolution picker instead of in a separate
   * actions row up in the page header.
   */
  toolbarRight?: React.ReactNode;
  /**
   * Fill the parent instead of self-sizing: root stretches to 100% of the
   * container and the stage takes the remaining height (instead of the
   * legacy 60vh). Used by the workspace's resizable preview pane so the
   * player tracks pane/window resizes. Height-less contexts (TemplateDetail,
   * modal) keep the default.
   */
  fill?: boolean;
  /**
   * Optional portal targets. When set, the settings toolbar and/or the
   * playback bar render INTO these elements (chrome-less) instead of
   * inline — the workspace mounts them in the preview-card header and the
   * full-width bottom strip. All state stays inside this component.
   */
  toolbarPortal?: HTMLElement | null;
  playbackPortal?: HTMLElement | null;
  /**
   * When true, the portaled toolbar renders ONLY the Zoom/Fit control (plus
   * any `toolbarRight` slot, e.g. the Render button). The BG picker,
   * content-scale slider, and canvas-size dropdown are hidden — TemplateDetail
   * relocates those into its Settings tab. Their underlying state (read from
   * localStorage) still drives the stage/Player here, so the Settings-tab
   * controls stay in lockstep with the preview. PromptWorkspace omits this
   * prop and keeps the full toolbar.
   */
  toolbarZoomOnly?: boolean;
}

const ZOOM_LEVELS = [
  { label: "25%", value: 0.25 },
  { label: "50%", value: 0.5 },
  { label: "75%", value: 0.75 },
  { label: "100%", value: 1 },
  { label: "Fit", value: 0 },
];

export const BG_OPTIONS = [
  { label: "Dark", value: "#0a0a0a" },
  { label: "Checker", value: "checker" },
  { label: "White", value: "#ffffff" },
  { label: "Green", value: "#00ff00" },
  { label: "Blue", value: "#0000ff" },
];

const CHECKER_BG =
  "repeating-conic-gradient(#1a1a1a 0% 25%, #2a2a2a 0% 50%) 50% / 20px 20px";

export function RemotionPreview(props: RemotionPreviewProps) {
  // Read the legacy flag exactly once on mount. Toggling is a deliberate
  // operator action — we don't want HMR or React re-renders to flip the
  // engine mid-session.
  const [useLegacy] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("motionforge-preview-legacy") === "1";
  });
  if (useLegacy) {
    return <RemotionPreviewLegacy {...props} />;
  }
  return <RemotionPreviewLean {...props} />;
}

function RemotionPreviewLean({
  moduleUrl,
  inputProps,
  durationFrames,
  fps,
  width,
  height,
  contentScale: contentScaleProp,
  baseWidth,
  baseHeight,
  onPlayerRef,
  toolbarRight,
  fill = false,
  toolbarPortal,
  playbackPortal,
  toolbarZoomOnly = false,
}: RemotionPreviewProps) {
  // Persist content scale via the same localStorage slot RenderButton uses
  // so the preview slider and render settings stay in sync.
  const [renderSettings, setRenderSettings] = useLocalStorage<{
    content_scale?: number;
    [k: string]: unknown;
  }>("motionforge-render-settings", {});
  const storedScale = renderSettings.content_scale ?? 1;
  const contentScale = contentScaleProp ?? storedScale;
  const setContentScale = useCallback(
    (v: number) => setRenderSettings((prev) => ({ ...prev, content_scale: v })),
    [setRenderSettings],
  );

  // Preview chrome state persists across mounts (matches legacy behavior).
  const [zoom, setZoom] = useLocalStorage<number>("motionforge-preview-zoom", 0); // 0 = fit
  const [bg, setBg] = useLocalStorage<string>("motionforge-preview-bg", "checker");

  const Component = useDynamicComponent(moduleUrl);
  // Wrap the dynamically loaded component with the same scaling HOC the
  // CLI render uses so the slider visibly shrinks the preview the way it
  // shrinks the exported video. Memoized on Component so the wrapper
  // identity doesn't churn between renders (would force Player remount).
  const ScaledComponent = useMemo(
    () =>
      Component
        ? withScaling(Component as React.ComponentType<Record<string, unknown>>)
        : null,
    [Component],
  );
  const playerRef = useRef<PlayerRef>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

  // Track the stage's content-box so "Fit" can recompute on every layout
  // change (window resize, sidebar collapse, fullscreen toggle, etc.).
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setStageSize({ w: cr.width, h: cr.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Forward the Player ref to the parent. Polls briefly so callers (e.g.
  // thumbnail capture) reliably see a non-null ref even when Remotion's
  // internal ref-assignment trails React's commit.
  useEffect(() => {
    if (!onPlayerRef) return;
    let cancelled = false;
    const start = Date.now();
    const tick = () => {
      if (cancelled) return;
      if (playerRef.current) {
        onPlayerRef(playerRef.current);
        return;
      }
      if (Date.now() - start < 3000) setTimeout(tick, 100);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [moduleUrl, onPlayerRef]);

  // Compute the rendered display size from zoom + stage dimensions. zoom===0
  // means "Fit" — scale uniformly until the composition fills the stage.
  const displaySize = useMemo(() => {
    if (zoom > 0) {
      return { w: width * zoom, h: height * zoom };
    }
    if (stageSize.w === 0 || stageSize.h === 0) {
      return { w: width, h: height };
    }
    const scale = Math.min(stageSize.w / width, stageSize.h / height);
    return { w: width * scale, h: height * scale };
  }, [zoom, stageSize, width, height]);

  // Background style. "checker" expands to the gradient; everything else
  // is a CSS color string passed straight through.
  const stageBg = bg === "checker" ? CHECKER_BG : bg;

  // Toolbar — MotionForge-specific render settings. Chrome-less when
  // portaled into the preview-card header; carded when rendered inline.
  const toolbar = (
      <div
        className={
          toolbarPortal
            ? "flex min-w-0 flex-wrap items-center justify-end gap-3 text-xs"
            : "flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs"
        }
      >
        <ToolbarMenu
          label="Zoom"
          value={zoom}
          options={ZOOM_LEVELS}
          renderValue={(v) =>
            ZOOM_LEVELS.find((o) => o.value === v)?.label ?? "Fit"
          }
          onChange={setZoom}
        />
        {/* BG picker + content-scale slider — hidden in zoom-only mode
            (TemplateDetail relocates these into its Settings tab). Their
            state still drives the stage/Player above; only the controls
            move. */}
        {!toolbarZoomOnly && (
          <>
            <ToolbarMenu
              label="BG"
              value={bg}
              options={BG_OPTIONS}
              renderValue={(v) =>
                BG_OPTIONS.find((o) => o.value === v)?.label ?? "Checker"
              }
              onChange={setBg}
            />
            <label
              className="flex items-center gap-2 text-ink-2"
              title="Content scale — shrinks the graphic to fit overflowing text. Linked to render settings."
            >
              <span>Scale:</span>
              <input
                type="range"
                min={0.3}
                max={1}
                step={0.05}
                value={contentScale}
                onChange={(e) => setContentScale(parseFloat(e.target.value))}
                className="accent-accent"
                title="Content scale — shrinks the graphic to fit overflowing text. Linked to render settings."
              />
              <span className="w-10 text-right text-ink-2">
                {Math.round(contentScale * 100)}%
              </span>
            </label>
          </>
        )}
        {/* Canvas size + FPS picker — same component the legacy preview
            used. Writes to motionforge-render-settings so preview and
            render share the same source of truth. Hidden in zoom-only mode
            (render-output resolution lives in the Settings tab's render
            section there). */}
        <div className="ml-auto flex items-center gap-2">
          {!toolbarZoomOnly && (
            <ResolutionTrigger
              width={width}
              height={height}
              fps={fps}
              baseWidth={baseWidth}
              baseHeight={baseHeight}
            />
          )}
          {toolbarRight}
        </div>
      </div>
  );

  const playback = (
    <PlaybackBar
      playerRef={playerRef}
      durationFrames={durationFrames}
      fps={fps}
      bare={!!playbackPortal}
    />
  );

  return (
    <div
      className={
        fill
          ? "flex h-full w-full min-h-0 min-w-0 flex-col gap-2"
          : "flex flex-col gap-2"
      }
    >
      {toolbarPortal ? createPortal(toolbar, toolbarPortal) : toolbar}

      {/* Stage — bounded box that the Player composition scales into.
          The Player's container gets a green outline + a huge inset
          box-shadow that paints solid black over the area outside the
          composition's bounds. This is the "letterbox" treatment from
          the legacy preview: any element drawing past the canvas (a
          shape positioned at width+200, an image larger than the comp,
          etc.) appears clearly clipped, so the user can see exactly
          what the renderer will and won't capture. */}
      <div
        ref={stageRef}
        className={`relative flex items-center justify-center overflow-hidden rounded-lg border border-line ${
          fill ? "min-h-0 flex-1" : "h-[60vh] min-h-[320px]"
        }`}
        style={{ background: stageBg }}
      >
        {ScaledComponent ? (
          <div
            style={{
              width: displaySize.w,
              height: displaySize.h,
              position: "relative",
              overflow: "hidden",
              outline: "1px solid rgba(0, 255, 65, 0.35)",
              // 9999px box-shadow paints the area outside the comp solid
              // black, masking any out-of-bounds elements without
              // clipping them at the source (so screenshots / debugging
              // can still see what's there if needed via dev tools).
              boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.55)",
            }}
          >
            <Player
              ref={playerRef}
              component={ScaledComponent}
              inputProps={{
                ...inputProps,
                __contentScale: contentScale,
                __baseWidth: baseWidth,
                __baseHeight: baseHeight,
                __compWidth: width,
                __compHeight: height,
              }}
              durationInFrames={durationFrames}
              fps={fps}
              compositionWidth={width}
              compositionHeight={height}
              style={{ width: "100%", height: "100%" }}
              // controls={false}: the native bottom overlay covers the
              // bottom of the composition. We render our own bar BELOW
              // the stage instead — see PlaybackBar below. Everything
              // else (clickToPlay / spaceKey / fullscreen API) is kept
              // because those are interaction conveniences without a
              // visual overlay.
              controls={false}
              allowFullscreen
              clickToPlay
              doubleClickToFullscreen
              spaceKeyToPlayOrPause
              loop
              acknowledgeRemotionLicense
            />
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-line border-t-accent" />
            <span className="text-sm text-ink-2">
              Loading preview…
            </span>
          </div>
        )}
      </div>

      {/* External playback bar — below the stage, or portaled into the
          workspace's full-width bottom strip. Wires to the Player via ref
          methods + frameupdate events; no DOM-of-the-Player traversal. */}
      {playbackPortal ? createPortal(playback, playbackPortal) : playback}
    </div>
  );
}

/**
 * External playback bar. Stays in sync with the Player by listening for
 * `play` / `pause` / `frameupdate` events. All actions go through the
 * Player ref's imperative API. Lives outside the stage so the composition
 * is never partially hidden by playback chrome.
 */
function PlaybackBar({
  playerRef,
  durationFrames,
  fps,
  bare = false,
}: {
  playerRef: React.RefObject<PlayerRef | null>;
  durationFrames: number;
  fps: number;
  /** Skip the card chrome — the portal container provides it. */
  bare?: boolean;
}) {
  const [frame, setFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Subscribe once the Player ref attaches. We poll briefly because
  // Remotion's ref-assignment trails React's commit by one tick on
  // some renders.
  useEffect(() => {
    let cancelled = false;
    let detach: (() => void) | undefined;
    const start = Date.now();
    const tick = () => {
      if (cancelled) return;
      const p = playerRef.current;
      if (!p) {
        if (Date.now() - start < 3000) setTimeout(tick, 100);
        return;
      }
      const onPlay = () => setIsPlaying(true);
      const onPause = () => setIsPlaying(false);
      const onFrame = (e: { detail: { frame: number } }) =>
        setFrame(e.detail.frame);
      p.addEventListener("play", onPlay);
      p.addEventListener("pause", onPause);
      p.addEventListener("frameupdate", onFrame);
      setIsPlaying(p.isPlaying());
      setFrame(p.getCurrentFrame());
      detach = () => {
        p.removeEventListener("play", onPlay);
        p.removeEventListener("pause", onPause);
        p.removeEventListener("frameupdate", onFrame);
      };
    };
    tick();
    return () => {
      cancelled = true;
      detach?.();
    };
  }, [playerRef]);

  const togglePlay = useCallback(() => {
    playerRef.current?.toggle();
  }, [playerRef]);
  const onScrub = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      playerRef.current?.seekTo(parseInt(e.target.value, 10));
    },
    [playerRef],
  );
  const onFullscreen = useCallback(() => {
    playerRef.current?.requestFullscreen();
  }, [playerRef]);

  // User-selectable display format. Persisted so a session preference
  // survives reloads. Default is "frames" because that's what render
  // settings + Remotion compositions actually reason about — seconds is a
  // human-friendly view of the same number.
  const [timeFormat, setTimeFormat] = useLocalStorage<TimeFormat>(
    "motionforge-preview-time-format",
    "frames",
  );

  // Format a single frame value. Returns just the number/text for one
  // side of the readout — caller composes "current / total".
  const fmtOne = useCallback(
    (f: number) => {
      switch (timeFormat) {
        case "frames":
          return `${f}f`;
        case "seconds":
          return `${(f / fps).toFixed(2)}s`;
        case "timecode": {
          const totalSeconds = Math.floor(f / fps);
          const mm = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
          const ss = String(totalSeconds % 60).padStart(2, "0");
          return `${mm}:${ss}`;
        }
      }
    },
    [timeFormat, fps],
  );

  const max = Math.max(0, durationFrames - 1);
  // Bare mode (the workspace's bottom strip) is as thin as it can get:
  // small square controls, no card chrome, tight vertical rhythm.
  const btnClass = bare
    ? "flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs transition-colors hover:bg-surface-3"
    : "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-surface text-base transition-colors hover:bg-surface-3";
  return (
    <div
      className={
        bare
          ? "flex w-full min-w-0 items-center gap-3 text-xs"
          : "flex items-center gap-4 rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm"
      }
    >
      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? "Pause" : "Play"}
        className={`${btnClass} text-ink`}
      >
        {isPlaying ? "❙❙" : "▶"}
      </button>
      <input
        type="range"
        min={0}
        max={max}
        step={1}
        value={frame}
        onChange={onScrub}
        className={`flex-1 accent-accent ${bare ? "h-1.5" : "h-2"}`}
        aria-label="Seek"
      />
      {/* Time readout + format-menu trigger as a single tight unit.
          gap-0.5 puts the ▾ arrow flush against the digits so it reads
          as a single control, not a separate button. Fixed min-width on
          the readout keeps the surrounding bar from reshuffling as the
          digit count changes. */}
      <div className="flex shrink-0 items-center gap-0.5">
        <span
          className={`inline-block text-right tabular-nums text-ink ${bare ? "min-w-[130px]" : "min-w-[170px]"}`}
        >
          {fmtOne(frame)} / {fmtOne(max)}
        </span>
        <TimeFormatMenu value={timeFormat} onChange={setTimeFormat} />
      </div>
      {/* Player fullscreen — the one true maximize. */}
      <button
        type="button"
        onClick={onFullscreen}
        aria-label="Fullscreen"
        className={`${btnClass} text-ink-2 hover:text-ink`}
      >
        ⛶
      </button>
    </div>
  );
}

type TimeFormat = "frames" | "timecode" | "seconds";

/**
 * Reusable label + ▾ dropdown for compact toolbar settings (Zoom, BG).
 * Generic over the option value so callers don't need to coerce.
 */
function ToolbarMenu<T extends string | number>({
  label,
  value,
  options,
  renderValue,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { label: string; value: T }[];
  renderValue: (v: T) => string;
  onChange: (v: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2 py-1 text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
      >
        <span className="text-ink-3">{label}:</span>
        <span>{renderValue(value)}</span>
        <span className="text-[10px] text-ink-3">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 min-w-[120px] overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg">
          {options.map((opt) => (
            <button
              type="button"
              key={String(opt.value)}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`block w-full px-3 py-1.5 text-left text-xs transition-colors ${
                opt.value === value
                  ? "bg-accent-soft text-accent"
                  : "text-ink-2 hover:bg-surface-3 hover:text-ink"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Trigger button + popover for the canvas-size / FPS picker. Reuses the
 * legacy ResolutionDropdown component verbatim — the heavy logic
 * (presets, custom inputs, aspect hint, render-settings sync) is too
 * valuable to duplicate.
 */
function ResolutionTrigger({
  width,
  height,
  fps,
  baseWidth,
  baseHeight,
}: {
  width: number;
  height: number;
  fps: number;
  baseWidth?: number;
  baseHeight?: number;
}) {
  const [open, setOpen] = useState(false);
  const [renderSettings] = useLocalStorage<{
    width?: number;
    height?: number;
    fps?: number;
    [k: string]: unknown;
  }>("motionforge-render-settings", {});
  const w = (renderSettings.width as number | undefined) ?? width;
  const h = (renderSettings.height as number | undefined) ?? height;
  const f = (renderSettings.fps as number | undefined) ?? fps;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2 py-1 text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
        title="Change canvas size and frame rate"
        aria-expanded={open}
      >
        <span>
          {w}×{h} @ {f}fps
        </span>
        <span className="text-[10px] text-ink-3">▾</span>
      </button>
      {open && (
        <ResolutionDropdown
          baseWidth={baseWidth}
          baseHeight={baseHeight}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

const TIME_FORMAT_LABELS: Record<TimeFormat, string> = {
  frames: "Frames",
  timecode: "Time",
  seconds: "Seconds",
};

/**
 * Compact arrow-only menu for picking the time format. The currently
 * selected format is visible in the readout next to it ("0f", "00:00",
 * "0.00s"), so the menu trigger doesn't repeat the label — just a ▾.
 */
function TimeFormatMenu({
  value,
  onChange,
}: {
  value: TimeFormat;
  onChange: (v: TimeFormat) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside mousedown so the menu disappears before any
  // sibling element fires its click — same pattern as TemplateCardMenu.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-label="Time format"
        title={`Time format: ${TIME_FORMAT_LABELS[value]}`}
        onClick={() => setOpen((v) => !v)}
        className="flex h-10 w-6 items-center justify-center text-lg text-ink-3 transition-colors hover:text-ink"
      >
        ▾
      </button>
      {open && (
        // Anchored upward (`bottom-full`) because the playback bar lives
        // at the bottom edge of a tall preview area — opening downward
        // would clip into the tabs below.
        <div className="absolute bottom-full right-0 z-20 mb-1 min-w-[110px] overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg">
          {(Object.keys(TIME_FORMAT_LABELS) as TimeFormat[]).map((opt) => (
            <button
              type="button"
              key={opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className={`block w-full px-3 py-1.5 text-left text-xs transition-colors ${
                opt === value
                  ? "bg-accent-soft text-accent"
                  : "text-ink-2 hover:bg-surface-3 hover:text-ink"
              }`}
            >
              {TIME_FORMAT_LABELS[opt]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
